import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash, Key, ShieldCheck } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useAuth, roleLabel } from "../../context/AuthContext";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

const field = "rounded-none border-navy/20 bg-white h-11 focus-visible:ring-2 focus-visible:ring-gold focus-visible:border-gold";
const ROLES = ["master", "editor_live", "editor_review"];

const RoleSelect = ({ value, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    className="rounded-none border border-navy/20 bg-white h-10 px-3 text-sm focus:ring-2 focus:ring-gold outline-none">
    {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
  </select>
);

export const UsersPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "editor_review" });
  const [pwById, setPwById] = useState({});

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const err = (e, fb) => toast.error(e?.response?.data?.detail || fb);

  const addUser = async () => {
    if (!form.email || !form.name || !form.password) { toast.error("All fields are required"); return; }
    try {
      await api.post("/admin/users", form);
      setForm({ email: "", name: "", password: "", role: "editor_review" });
      load(); toast.success("User created");
    } catch (e) { err(e, "Failed to create user"); }
  };
  const changeRole = async (u, role) => {
    try { await api.put(`/admin/users/${u.id}`, { role }); load(); toast.success(`${u.email} is now ${roleLabel(role)}`); }
    catch (e) { err(e, "Failed to update role"); }
  };
  const toggleActive = async (u, active) => {
    try { await api.put(`/admin/users/${u.id}`, { active }); load(); toast.success(active ? "User enabled" : "User disabled"); }
    catch (e) { err(e, "Failed to update"); }
  };
  const resetPw = async (u) => {
    const pw = pwById[u.id];
    if (!pw || pw.length < 6) { toast.error("Enter a password (min 6 chars)"); return; }
    try { await api.put(`/admin/users/${u.id}/password`, { password: pw }); setPwById({ ...pwById, [u.id]: "" }); toast.success("Password updated"); }
    catch (e) { err(e, "Failed to reset password"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); load(); toast.success("User deleted"); }
    catch (e) { err(e, "Failed to delete"); }
  };

  return (
    <div className="space-y-8" data-testid="users-panel">
      <div className="bg-white border border-navy/10 p-6">
        <h3 className="font-heading text-xl text-navy mb-4 flex items-center gap-2"><UserPlus size={22} /> Add a new user</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input data-testid="new-user-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
          <Input data-testid="new-user-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
          <Input data-testid="new-user-password" type="text" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
          <RoleSelect testid="new-user-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
        </div>
        <button onClick={addUser} data-testid="add-user-btn" className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-6 py-3 rounded-sm hover:bg-gold hover:text-navy transition-colors">
          <UserPlus size={18} /> Create user
        </button>
      </div>

      <div className="space-y-4">
        {users.map((u) => (
          <div key={u.id} className="bg-white border border-navy/10 p-5" data-testid={`user-row-${u.email}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {u.role === "master" && <ShieldCheck size={20} weight="fill" className="text-gold" />}
                <div>
                  <div className="font-medium text-navy">{u.name} {u.id === user?.id && <span className="text-xs text-navy/40">(you)</span>}</div>
                  <div className="text-sm text-navy/50">{u.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <RoleSelect testid={`role-${u.email}`} value={u.role} onChange={(v) => changeRole(u, v)} />
                <label className="flex items-center gap-2 text-sm text-navy/70">
                  {u.active ? "Active" : "Disabled"}
                  <Switch data-testid={`active-${u.email}`} checked={u.active} onCheckedChange={(v) => toggleActive(u, v)} />
                </label>
                <button onClick={() => del(u)} data-testid={`delete-user-${u.email}`} className="text-navy/40 hover:text-red-500 transition-colors"><Trash size={20} /></button>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-navy/10">
              <Key size={18} className="text-navy/40" />
              <Input data-testid={`pw-${u.email}`} type="text" placeholder="Set new password" value={pwById[u.id] || ""} onChange={(e) => setPwById({ ...pwById, [u.id]: e.target.value })} className="rounded-none border-navy/20 bg-white h-10 max-w-xs" />
              <button onClick={() => resetPw(u)} data-testid={`set-pw-${u.email}`} className="text-sm font-semibold border border-navy/20 px-4 py-2 rounded-sm hover:border-gold transition-colors">Update password</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
